// Shared line-icon set for navigation + cards. Single stroke weight,
// 24×24 viewBox, `currentColor` so each icon inherits the text colour of
// its slot (teal in a card tile, muted/white in the sidebar). All are
// `aria-hidden` — the adjacent label carries the meaning (DESIGN §1.4 #8:
// icons are decorative, copy is authoritative).
//
// Kept deliberately small and hand-rolled rather than pulling an icon
// library: the design language uses a consistent, simple line style and a
// dependency would drag in hundreds of unused glyphs.

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Base({ size = 20, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable={false}
      {...rest}
    >
      {children}
    </svg>
  );
}

export function GridIcon(p: IconProps) {
  return (
    <Base {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </Base>
  );
}

export function UsersIcon(p: IconProps) {
  return (
    <Base {...p}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.2a3.2 3.2 0 0 1 0 5.6" />
      <path d="M17.5 20a5.5 5.5 0 0 0-2.6-4.7" />
    </Base>
  );
}

export function UserIcon(p: IconProps) {
  return (
    <Base {...p}>
      <circle cx="12" cy="8" r="3.4" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" />
    </Base>
  );
}

export function PlusIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M12 5v14M5 12h14" />
    </Base>
  );
}

export function DocIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4" />
      <path d="M9 12h6M9 16h6" />
    </Base>
  );
}

export function BookIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H18v14H5.5A1.5 1.5 0 0 0 4 19.5z" />
      <path d="M4 19.5A1.5 1.5 0 0 1 5.5 18H18" />
    </Base>
  );
}

export function ChatIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M4 5h16v11H9l-4 3v-3H4z" />
    </Base>
  );
}

export function PlugIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M9 3v5M15 3v5" />
      <path d="M7 8h10v3a5 5 0 0 1-10 0z" />
      <path d="M12 16v5" />
    </Base>
  );
}

export function HomeIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M4 11.5 12 4l8 7.5" />
      <path d="M6 10v10h12V10" />
    </Base>
  );
}

export function CalendarIcon(p: IconProps) {
  return (
    <Base {...p}>
      <rect x="4" y="5" width="16" height="16" rx="2" />
      <path d="M4 9h16M8 3v4M16 3v4" />
    </Base>
  );
}

export function HeartIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M12 20s-7-4.5-9-9a4.2 4.2 0 0 1 7.8-2.2L12 10l1.2-1.2A4.2 4.2 0 0 1 21 11c-2 4.5-9 9-9 9z" />
    </Base>
  );
}

export function ShieldIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M12 3 5 6v6c0 4.5 3 7.5 7 9 4-1.5 7-4.5 7-9V6z" />
    </Base>
  );
}

export function SlidersIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M5 8h9M18 8h1M5 16h1M10 16h9" />
      <circle cx="16" cy="8" r="2" />
      <circle cx="8" cy="16" r="2" />
    </Base>
  );
}

export function PulseIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M3 12h4l2.5-6 4 12L16 12h5" />
    </Base>
  );
}

export function BarsIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M6 20V10M12 20V4M18 20v-6" />
    </Base>
  );
}

export function LifebuoyIcon(p: IconProps) {
  return (
    <Base {...p}>
      <circle cx="12" cy="12" r="8.5" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="M6 6l3.6 3.6M14.4 14.4 18 18M18 6l-3.6 3.6M9.6 14.4 6 18" />
    </Base>
  );
}

export function ChevronRightIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M9 5l7 7-7 7" />
    </Base>
  );
}

export function ChevronLeftIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M15 5l-7 7 7 7" />
    </Base>
  );
}

export function SendIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M4 12 20 4l-6.5 16-2.5-7-7-1z" strokeLinejoin="round" />
    </Base>
  );
}

export function TrainingIcon(p: IconProps) {
  return (
    <Base {...p}>
      <path d="M3 8.5 12 4l9 4.5-9 4.5z" />
      <path d="M7 10.5V15c0 1.4 2.2 2.5 5 2.5s5-1.1 5-2.5v-4.5" />
    </Base>
  );
}
